/**
 * use-validation-rules — OPEN-217 (DEC-382) driver entry rules, the editor's data.
 *
 * GET /v1/validation-rules returns every job type family (its live version's name and shape,
 * active or not) and every rule, each marked `applies: false` when a later job type edit means
 * the driver can no longer enter its field. The editor never calls /v1/job-types, which billing
 * roles can't read.
 *
 * Writes go through `saveValidationRule` / `deleteValidationRule` and invalidate this key. The
 * API refuses a rule that doesn't fit with `error.fields[].reason` in plain words, which the
 * editor shows as-is.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiErrorFromResponse, authedFetch, type WebApiOpts } from '@lodex-pro/web-shared';
import type {
  ValidationRuleCheckKind,
  ValidationRuleField,
  ValidationRuleSeverity,
  ValidationShape,
} from '@lodex-pro/shared/validation-rules';

/** A job type family, by its live version. */
export interface RuleFamily {
  readonly familyId: string;
  readonly jobTypeId: string | number;
  readonly code: string;
  readonly label: string;
  readonly isActive: boolean;
  readonly shape: ValidationShape;
}

export interface EditorRule {
  readonly id: string;
  readonly familyId: string;
  readonly field: ValidationRuleField;
  readonly checkKind: ValidationRuleCheckKind;
  readonly minValue: number | null;
  readonly maxValue: number | null;
  readonly message: string;
  readonly severity: ValidationRuleSeverity;
  readonly serviceChargeId: string | null;
  readonly serviceChargeLabel: string | null;
  readonly serviceChargeStage: string | null;
  readonly isActive: boolean;
  readonly sortOrder: number;
  /** False when the driver can no longer enter this field on the live version. */
  readonly applies: boolean;
  readonly updatedAt: string;
  readonly updatedByName: string | null;
}

export interface ValidationRulesResponse {
  readonly families: readonly RuleFamily[];
  readonly rules: readonly EditorRule[];
}

export const VALIDATION_RULES_QUERY_KEY = ['validation-rules', 'editor'] as const;

export function useValidationRules(apiOpts: WebApiOpts): UseQueryResult<ValidationRulesResponse> {
  return useQuery<ValidationRulesResponse>({
    queryKey: VALIDATION_RULES_QUERY_KEY,
    queryFn: async () => {
      const res = await authedFetch(apiOpts, '/v1/validation-rules');
      if (!res.ok) throw await apiErrorFromResponse(res, '/v1/validation-rules');
      return (await res.json()) as ValidationRulesResponse;
    },
  });
}

/** The body a create (with familyId) or an edit sends. */
export interface RuleBody {
  readonly familyId?: string;
  readonly field: ValidationRuleField;
  readonly checkKind: ValidationRuleCheckKind;
  readonly minValue: number | null;
  readonly maxValue: number | null;
  readonly message: string;
  readonly severity: ValidationRuleSeverity;
  readonly serviceChargeId: string | null;
}

export type SaveResult = { readonly ok: true } | { readonly ok: false; readonly error: string };

/** The API's own words for a refusal, or a status line when it sent none. */
async function refusal(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as {
      error?: { message?: string; fields?: ReadonlyArray<{ reason?: string }> };
    };
    const reason = body.error?.fields?.find((f) => typeof f.reason === 'string')?.reason;
    if (reason !== undefined) return reason;
    if (res.status === 403) return 'You don’t have permission to change driver entry rules.';
    if (typeof body.error?.message === 'string') return body.error.message;
  } catch {
    // Not JSON: fall through to the status line.
  }
  return `Save failed (${String(res.status)}).`;
}

/** POST a new rule (body carries familyId) or PATCH an existing one. */
export async function saveValidationRule(
  apiOpts: WebApiOpts,
  ruleId: string | null,
  body: RuleBody,
): Promise<SaveResult> {
  try {
    const res = await authedFetch(
      apiOpts,
      ruleId === null ? '/v1/validation-rules' : `/v1/validation-rules/${ruleId}`,
      { method: ruleId === null ? 'POST' : 'PATCH', body: JSON.stringify(body) },
    );
    return res.ok ? { ok: true } : { ok: false, error: await refusal(res) };
  } catch {
    return { ok: false, error: 'Network error — please try again.' };
  }
}

export async function deleteValidationRule(
  apiOpts: WebApiOpts,
  ruleId: string,
): Promise<SaveResult> {
  try {
    // DEC-201 — authedFetch always sends Content-Type: application/json, and the API refuses an
    // empty JSON body, so a DELETE sends {}.
    const res = await authedFetch(apiOpts, `/v1/validation-rules/${ruleId}`, {
      method: 'DELETE',
      body: JSON.stringify({}),
    });
    return res.ok ? { ok: true } : { ok: false, error: await refusal(res) };
  } catch {
    return { ok: false, error: 'Network error — please try again.' };
  }
}
